import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL, resolveUploadUrl } from "../config";
import { FaUser, FaEnvelope, FaPhone, FaShieldAlt, FaSave, FaEdit, FaLock, FaCalendarAlt, FaCheckCircle } from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import ChangePasswordModal from '../components/ChangePasswordModal';
import { toast } from 'react-toastify';

function ProfilePage({ onLogout }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({ full_name: '', phone: '' });
  const [message, setMessage] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarError, setAvatarError] = useState(false);

  // Reset the error fallback whenever the avatar URL changes
  useEffect(() => { setAvatarError(false); }, [avatarUrl]);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      console.log('🔑 Token exists?', token ? 'Yes' : 'No');
      
      // ✅ First, try to get user from localStorage (already works)
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (storedUser.id) {
        setUser(storedUser);
        setFormData({
          full_name: storedUser.full_name || '',
          phone: storedUser.phone || ''
        });
        setAvatarUrl(storedUser.avatar_url || '');
        console.log('📊 Using localStorage user data:', storedUser);
      }

      // ✅ Then try to fetch fresh data from API (if token exists)
      if (token) {
        try {
          const response = await fetch(`${API_URL}/users/me`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          const data = await response.json();
          console.log('📊 API Profile data:', data);
          
          if (data.success || data.user) {
            const userData = data.user || data;
            setUser(userData);
            setFormData({
              full_name: userData.full_name || '',
              phone: userData.phone || ''
            });
            setAvatarUrl(userData.avatar_url || '');
            // Update localStorage with fresh data
            localStorage.setItem('user', JSON.stringify(userData));
          }
        } catch (apiError) {
          console.log('⚠️ API fetch failed, using localStorage data');
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Please login again');
        return;
      }

      const response = await fetch(`${API_URL}/users/${currentUser.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          full_name: formData.full_name,
          phone: formData.phone,
          role: user?.role
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        setEditMode(false);
        fetchProfile();
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        storedUser.full_name = formData.full_name;
        localStorage.setItem('user', JSON.stringify(storedUser));
        toast.success('✅ Profile updated successfully!');
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update profile' });
        toast.error(data.error || 'Failed to update profile');
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile' });
      toast.error('Failed to update profile');
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      toast.error('Please choose a JPG, PNG, GIF or WebP image');
      return;
    }
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const uploadRes = await fetch(`${API_URL}/uploads`, { method: 'POST', body: formData });
      const uploadData = await uploadRes.json();
      if (!uploadData.success || !uploadData.url) {
        toast.error(uploadData.error || 'Upload failed');
        return;
      }
      const res = await fetch(`${API_URL}/users/${currentUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        // Store the RELATIVE /uploads/... path (environment-agnostic).
        // resolveUploadUrl() turns it into the right absolute URL at render time.
        body: JSON.stringify({ avatar_url: uploadData.url })
      });
      const data = await res.json();
      if (data.success) {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        storedUser.avatar_url = uploadData.url;
        localStorage.setItem('user', JSON.stringify(storedUser));
        setAvatarUrl(uploadData.url);
        setUser({ ...user, avatar_url: uploadData.url });
        toast.success('✅ Profile photo updated');
      } else {
        toast.error(data.error || 'Failed to save profile photo');
      }
    } catch (error) {
      console.error('Avatar upload error:', error);
      toast.error('Failed to upload profile photo');
    } finally {
      setAvatarUploading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-ZA', { 
        day: '2-digit', 
        month: 'long', 
        year: 'numeric' 
      });
    } catch {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <AdminLayout activeTab="profile" onLogout={onLogout} user={currentUser}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4">🔄</div>
            <p className="text-gray-500">Loading profile...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="profile" onLogout={onLogout} user={currentUser}>
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-navy">👤 My Profile</h1>
            <p className="text-gray-500 text-sm">View and manage your account information</p>
          </div>
          <button
            onClick={() => setShowPasswordModal(true)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#2563EB',
              color: 'white',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1D4ED8'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
          >
            <FaLock size={14} /> Change Password
          </button>
        </div>

        {message && (
          <div className={`p-4 rounded-lg mb-4 ${message.type === 'success' ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
            {message.text}
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-semibold text-navy">Profile Information</h3>
            <button
              onClick={() => setEditMode(!editMode)}
              className="flex items-center gap-2 px-3 py-1 border border-gray-200 rounded-lg text-sm hover:border-green hover:text-green transition-colors"
            >
              {editMode ? <FaSave /> : <FaEdit />} {editMode ? 'Cancel' : 'Edit'}
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center text-navy font-bold text-2xl" style={{ backgroundColor: '#00A86B' }}>
                {avatarUrl && !avatarError ? (
                  <img src={resolveUploadUrl(avatarUrl)} alt="Profile" onError={() => setAvatarError(true)} className="w-full h-full object-cover" />
                ) : (
                  user?.full_name?.charAt(0) || 'U'
                )}
              </div>
              <div>
                <p className="text-lg font-bold text-navy">{user?.full_name || 'Unknown'}</p>
                <p className="text-sm text-gray-500 capitalize">{user?.role || 'N/A'}</p>
                <label className="inline-flex items-center gap-1 mt-1 text-xs text-green-600 hover:text-green-700 cursor-pointer">
                  <FaEdit size={11} />
                  {avatarUploading ? 'Uploading...' : 'Upload profile photo'}
                  <input type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} disabled={avatarUploading} />
                </label>
              </div>
            </div>

            <form onSubmit={handleSave}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    />
                  ) : (
                    <p className="py-2 text-gray-700">{user?.full_name || 'N/A'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <p className="py-2 text-gray-700">{user?.email || 'N/A'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    />
                  ) : (
                    <p className="py-2 text-gray-700">{user?.phone || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <p className="py-2 text-gray-700 capitalize">{user?.role || 'N/A'}</p>
                </div>
              </div>

              {editMode && (
                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setEditMode(false)}
                    className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors flex items-center gap-2"
                  >
                    <FaSave className="inline" /> Save Changes
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>

        <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
            <FaShieldAlt className="text-green" /> Account Information
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Account Created</span>
              <span className="text-gray-700">{formatDate(user?.created_at)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Last Login</span>
              <span className="text-gray-700">{user?.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-500">Status</span>
              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${user?.is_active ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                {user?.is_active ? '✅ Active' : '❌ Inactive'}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm text-blue-700 flex items-center gap-2">
            <FaShieldAlt className="text-blue-500" />
            <span>
              <strong>Security Tip:</strong> For better security, make sure your password is at least 8 characters with uppercase, lowercase, numbers, and special characters.
            </span>
          </p>
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        userId={currentUser.id}
      />
    </AdminLayout>
  );
}

export default ProfilePage;
